import React, { useRef, useState, useEffect, useMemo } from "react";
import axios from "axios";
import ParivahanRCView from "../../Screens/Common/ParivahanRc";
import ParivahanRC2View from "../../Screens/Common/ParivahaanRc2";
import SmartRCView from "../../Screens/Common/SmartRc";
import DigiLockerRCView from "../../Screens/Common/DigiLockerRC";
import PortalModal from "../PortalModal";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { toast } from "react-toastify";

export default function ChasisToRC() {
  const [vehicleNo, setVehicleNo] = useState("");
  const [selectedRC, setSelectedRC] = useState("smartrc");
  const [submitted, setSubmitted] = useState(true);
  const [showCard, setShowCard] = useState(false);
  const [rcHistory, setRcHistory] = useState([]);
  const [allRcHistory, setAllRcHistory] = useState([]); // Store all history data
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [error, setError] = useState("");
  const [rcPrices, setRcPrices] = useState({
    parivaanrc: 0,
    parivaanrc2: 0,
    smartrc: 0,
    digilockerrc: 0,
  });
  const [rcAccess, setRcAccess] = useState({
    parivaanrc: false,
    parivaanrc2: false,
    smartrc: false,
    digilockerrc: false,
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    fromDate: "",
    toDate: "",
  });
  const itemsPerPage = 10;

  const [rcData, setRcData] = useState(null);

  const printRef = useRef(null);

  useEffect(() => {
    fetchRcData();
    // Do not reset selectedRC here, keep user's choice
  }, []);

  const fetchRcData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/services/access-check`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const access = response.data?.serviceAccess || [];
      const prices = response.data?.servicePrices || {};
      setRcAccess({
        parivaanrc: access.includes("parivaanrc_chassis"),
        parivaanrc2: access.includes("parivaanrc2_chassis"),
        smartrc: access.includes("smartrc_chassis"),
        digilockerrc: access.includes("digilockerrc_chassis"),
      });
      setRcPrices({
        parivaanrc: prices.parivaanrc_chassis,
        parivaanrc2: prices.parivaanrc2_chassis,
        smartrc: prices.smartrc_chassis,
        digilockerrc: prices.digilockerrc_chassis,
      });
    } catch (e) {
      console.error("Error fetching access data:", e);
      toast.error("Failed to load service access information");
    }
  };

  const fetchHistoryData = async () => {
    try {
      const endpoints = [
        "rc-chassis-parivaanrc",
        "rc-chassis-parivaanrc2",
        "rc-chassis-smartrc",
        "rc-chassis-digilockerrc",
      ];

      const responses = await Promise.all(
        endpoints.map((endpoint) =>
          axios.get(
            `${process.env.REACT_APP_API_URL}/services/get-document-records?service=${endpoint}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          )
        )
      );

      // Normalize and flatten all responses
      let combined = responses.flatMap((res, idx) => {
        const data = res.data || []; // your API already returns array
        return data.map((item) => ({
          id: item._id,
          service: endpoints[idx],
          vehicleNo: item.idNumber || "N/A", // 👈 normalize field
          documentType: item.documentType || "N/A", // 👈 normalize field
          date: item.createdAt || new Date(), // 👈 normalize field
          status: item.status || "pending", // normalize status
          amount: item.amountDeducted ?? 0,
        }));
      });

      // Deduplicate by vehicleNo + date
      const seen = new Set();
      const unique = combined.filter((item) => {
        const key = `${item.vehicleNo}-${item.date}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      // Sort descending by date
      unique.sort((a, b) => new Date(b.date) - new Date(a.date));

      setAllRcHistory(unique);
      setRcHistory(unique); // initial render before filters
    } catch (err) {
      console.error("Error fetching RC history:", err);
      toast.error("Failed to fetch RC history");
    }
  };

  useEffect(() => {
    fetchHistoryData();
  }, []);
  // useEffect(() => {
  //   fetchData();
  // }, []);

  const handleSubmit = async () => {
    // Only fetch access/prices, don't reset selectedRC
    await fetchRcData();

    if (!vehicleNo.trim()) {
      toast.error("Please enter vehicle number");
      return;
    }
    if (!selectedRC) {
      toast.error("Please select an RC type");
      return;
    }
    setShowConfirmationModal(true);
  };
  const proceedWithApiCall = async () => {
    if (!vehicleNo.trim()) {
      toast.error("Please enter vehicle number");
      return;
    }
    if (!selectedRC) {
      toast.error("Please select an RC type");
      return;
    }

    setIsLoading(true);
    setError("");
    setRcData(null);

    try {
      // Original API call

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/services/rc/chassis-to-rc`,
        {
          id_number: vehicleNo,
          rc_type: selectedRC,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success === false) {
        throw new Error(response.data.message || "Failed to fetch RC data");
      }
      // Use the correct data structure for rcData
      setRcData(response.data.data);

      console.log("RC Data:", response.data.data);

      setSubmitted(true);
      setShowCard(true);

      // Add to history
      const newEntry = {
        date: new Date().toLocaleString(),
        vehicleNo,
        documentType: selectedRC,
        status: "Success",
      };
      setRcHistory((prev) => [newEntry, ...prev]);

      toast.success("RC data fetched successfully");
      setShowConfirmationModal(false);
    } catch (err) {
      console.error("Error fetching RC data:", err);

      let errorMessage = "Failed to fetch RC data. Please try again.";
      if (err.response) {
        // Server responded with a status code outside 2xx range
        if (err.response.data && err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.status === 401) {
          errorMessage = "Unauthorized - Please login again";
        } else if (err.response.status === 403) {
          errorMessage = "Forbidden - You don't have permission";
        } else if (err.response.status === 404) {
          errorMessage = "Service not found";
        } else if (err.response.status === 500) {
          errorMessage = "Server error - Please try again later";
        }
      } else if (err.request) {
        // Request was made but no response received
        errorMessage = "Network error - Please check your connection";
      }

      toast.error(errorMessage);

      // Add failed attempt to history
      const newEntry = {
        date: new Date().toLocaleString(),
        vehicleNo,
        name: "N/A",
        details: "Failed to fetch",
        status: "Failed",
        error: errorMessage,
      };
      setRcHistory((prev) => [newEntry, ...prev]);
      setShowConfirmationModal(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadImage = async () => {
    if (!printRef.current) {
      toast.error("No document to download");
      return;
    }

    try {
      const cardElement = printRef.current;
      const canvas = await html2canvas(cardElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
      });

      // Convert to JPEG with compression
      const imgData = canvas.toDataURL("image/jpeg", 0.85);

      // Create download link
      const link = document.createElement("a");
      link.download = `RC-${vehicleNo}.jpg`;
      link.href = imgData;
      link.click();

      toast.success("Image downloaded successfully");
    } catch (err) {
      console.error("Error generating image:", err);
      toast.error("Failed to generate image. Please try again.");
    }
  };

  const handlePrint = async () => {
    if (!printRef.current) {
      toast.error("No document to print");
      return;
    }

    try {
      const cardElement = printRef.current;
      const canvas = await html2canvas(cardElement, {
        scale: 3,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("portrait", "pt", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const ratio = Math.min(pageWidth / imgWidth, pageHeight / imgHeight);
      const scaledWidth = imgWidth * ratio;
      const scaledHeight = imgHeight * ratio;

      const marginX = (pageWidth - scaledWidth) / 2;
      const marginY = 30; // 👈 Add 30pt space at the top

      pdf.addImage(imgData, "PNG", marginX, marginY, scaledWidth, scaledHeight);
      pdf.save(`RC-${vehicleNo}.pdf`);

      toast.success("PDF downloaded successfully");
    } catch (err) {
      console.error("Error generating PDF:", err);
      toast.error("Failed to generate PDF. Please try again.");
    }
  };

  const paginatedData = Array.isArray(rcHistory)
    ? rcHistory.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      )
    : [];

  const totalPages = Math.ceil(
    Array.isArray(rcHistory) ? rcHistory.length / itemsPerPage : 0
  );

  const handlePrev = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNext = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));

  const applyFilters = () => {
    let filteredData = [...allRcHistory];

    // Apply search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filteredData = filteredData.filter((item) =>
        item.vehicleNo.toLowerCase().includes(searchTerm)
      );
    }

    // Apply status filter
    if (filters.status) {
      filteredData = filteredData.filter(
        (item) => item.status.toLowerCase() === filters.status.toLowerCase()
      );
    }

    // Apply date range filter
    if (filters.fromDate || filters.toDate) {
      const fromDate = filters.fromDate ? new Date(filters.fromDate) : null;
      const toDate = filters.toDate ? new Date(filters.toDate) : null;

      // Set time components for proper date comparison
      if (fromDate) fromDate.setHours(0, 0, 0, 0);
      if (toDate) toDate.setHours(23, 59, 59, 999);

      filteredData = filteredData.filter((item) => {
        const itemDate = new Date(item.createdAt);
        let matches = true;

        if (fromDate && itemDate < fromDate) matches = false;
        if (toDate && itemDate > toDate) matches = false;

        return matches;
      });
    }

    setRcHistory(filteredData);
    setCurrentPage(1);
  };

  // Update your clearFilters function:
  const clearFilters = () => {
    setFilters({
      search: "",
      status: "",
      fromDate: "",
      toDate: "",
    });
    setRcHistory(allRcHistory);
    setCurrentPage(1);
  };

  useEffect(() => {
    applyFilters();
  }, [filters, allRcHistory]);

  const visiblePages = useMemo(() => {
    const delta = 2; // how many pages around current
    const pages = [];
    for (
      let i = Math.max(1, currentPage - delta);
      i <= Math.min(totalPages, currentPage + delta);
      i++
    ) {
      pages.push(i);
    }
    return pages;
  }, [currentPage, totalPages]);

  return (
    <>
      <div className="py-4 w-full mx-auto print:bg-white">
        {showCard && rcData && (
          <PortalModal>
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 print:static">
              <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto relative p-6 print:static print:block print:shadow-none print:p-0 print:max-h-full print:overflow-visible">
                <div className="my-4 flex justify-between">
                  <button
                    onClick={() => {
                      setShowCard(false);
                      setShowConfirmationModal(false);
                    }}
                    className="bg-gray-300 px-2 py-1 rounded hover:bg-gray-400 text-md"
                  >
                    Close
                  </button>
                  <button
                    onClick={handlePrint}
                    className="bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 sm:text-xs text-sm"
                  >
                    Download PDF
                  </button>
                  {(selectedRC === "parivaanrc" ||
                    selectedRC === "parivaanrc2") && (
                    <button
                      onClick={handleDownloadImage}
                      className="bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 ml-2  sm:text-xs  text-sm"
                    >
                      Download Image
                    </button>
                  )}
                </div>

                <div ref={printRef}>
                  {/* <CertificateScaler> */}
                  {selectedRC === "parivaanrc" && (
                    <ParivahanRCView data={rcData} />
                  )}
                  {selectedRC === "parivaanrc2" && (
                    <ParivahanRC2View data={rcData} />
                  )}
                  {selectedRC === "digilockerrc" && (
                    <DigiLockerRCView data={rcData} />
                  )}
                  {selectedRC === "smartrc" && <SmartRCView data={rcData} />}
                  {/* </CertificateScaler> */}
                </div>
              </div>
            </div>
          </PortalModal>
        )}

        {!showCard && (
          <>
            <div className="w-full mx-auto md:py-4">
              <div className="bg-blue-100 p-2">
                <h2 className="text-center text-xl font-semibold text-blue-700">
                  Registration Certificate [Chasis to RC]
                </h2>
              </div>

              <div className="flex flex-col md:justify-between gap-4 md:flex-row md:items-center pt-6">
                <input
                  type="text"
                  placeholder="Enter Chasis No"
                  value={vehicleNo}
                  onChange={(e) => setVehicleNo(e.target.value)}
                  className="w-1/2 border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                <div className="flex w-full gap-3">
                  <div className="w-1/4">
                    <label
                      className={`flex w-full items-center gap-1 ${
                        !rcAccess.parivaanrc
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer"
                      }`}
                    >
                      <input
                        type="radio"
                        name="rc"
                        value="parivaanrc"
                        checked={selectedRC === "parivaanrc"}
                        disabled={!rcAccess.parivaanrc}
                        onChange={() => setSelectedRC("parivaanrc")}
                      />
                      <span>
                        Parivahan RC <br />
                        <span className="text-red-400 text-sm">
                          ₹ {rcPrices.parivaanrc}
                        </span>
                      </span>
                    </label>
                  </div>
                  <div className="w-1/4">
                    <label
                      className={`flex w-full items-center gap-1 ${
                        !rcAccess.parivaanrc2
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer"
                      }`}
                    >
                      <input
                        type="radio"
                        name="rc"
                        value="parivaanrc2"
                        checked={selectedRC === "parivaanrc2"}
                        disabled={!rcAccess.parivaanrc2}
                        onChange={() => setSelectedRC("parivaanrc2")}
                      />
                      <span>
                        Parivahan RC 2 <br />
                        <span className="text-red-400 text-sm">
                          ₹ {rcPrices.parivaanrc2}
                        </span>
                      </span>
                    </label>
                  </div>
                  <div className="w-1/4">
                    <label
                      className={`flex w-full items-center gap-1 ${
                        !rcAccess.smartrc
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer"
                      }`}
                    >
                      <input
                        type="radio"
                        name="rc"
                        value="smartrc"
                        checked={selectedRC === "smartrc"}
                        disabled={!rcAccess.smartrc}
                        onChange={() => setSelectedRC("smartrc")}
                      />
                      <span>
                        Smart RC <br />
                        <span className="text-red-400 text-sm">
                          ₹ {rcPrices.smartrc}
                        </span>
                      </span>
                    </label>
                  </div>
                  <div className="w-1/4">
                    <label
                      className={`flex w-full items-center gap-1 ${
                        !rcAccess.digilockerrc
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer"
                      }`}
                    >
                      <input
                        type="radio"
                        name="rc"
                        value="digilockerrc"
                        checked={selectedRC === "digilockerrc"}
                        disabled={!rcAccess.digilockerrc}
                        onChange={() => setSelectedRC("digilockerrc")}
                      />
                      <span>
                        Digilocker RC <br />
                        <span className="text-red-400 text-sm">
                          ₹ {rcPrices.digilockerrc}
                        </span>
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-start mt-4">
                <button
                  onClick={handleSubmit}
                  disabled={isLoading || !selectedRC}
                  className={`bg-indigo-600 text-white py-2 px-6 rounded hover:bg-indigo-700 ${
                    isLoading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  {isLoading ? "Processing..." : "SUBMIT"}
                </button>
              </div>
            </div>
            {showConfirmationModal && !showCard && (
              <PortalModal>
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                  <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
                    <h3 className="text-lg font-semibold mb-4">
                      Confirm Payment
                    </h3>
                    <p className="mb-4">
                      This RC verification will cost you{" "}
                      <span className="text-red-600">
                        ₹{rcPrices[selectedRC]}
                      </span>
                      . Do you want to proceed?
                    </p>
                    <div className="flex justify-end gap-4">
                      <button
                        onClick={() => setShowConfirmationModal(false)}
                        className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={proceedWithApiCall}
                        disabled={isLoading}
                        className={`px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ${
                          isLoading ? "opacity-70 cursor-not-allowed" : ""
                        }`}
                      >
                        {isLoading ? "Processing..." : "Confirm & Pay"}
                      </button>
                    </div>
                  </div>
                </div>
              </PortalModal>
            )}

            <div className="w-full">
              <div className="bg-white p-4 shadow-md rounded-lg mb-4">
                <h3 className="text-lg font-semibold mb-4">
                  Current RC Results
                </h3>

                {/* Current Results Filters */}
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <h4 className="text-md font-medium mb-3">
                    Filter Current Results
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Search
                      </label>
                      <input
                        type="text"
                        placeholder="Search vehicle Chasis number"
                        value={filters.search}
                        onChange={(e) =>
                          setFilters({ ...filters, search: e.target.value })
                        }
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Status
                      </label>
                      <select
                        value={filters.status}
                        onChange={(e) =>
                          setFilters({ ...filters, status: e.target.value })
                        }
                        className="w-full border rounded px-3 py-2"
                      >
                        <option value="">All Status</option>
                        <option value="Success">Success</option>
                        <option value="Failed">Failed</option>
                        <option value="pending">Pending</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        From Date
                      </label>
                      <input
                        type="date"
                        value={filters.fromDate}
                        onChange={(e) =>
                          setFilters({ ...filters, fromDate: e.target.value })
                        }
                        className="w-full border rounded px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        To Date
                      </label>
                      <input
                        type="date"
                        value={filters.toDate}
                        onChange={(e) =>
                          setFilters({ ...filters, toDate: e.target.value })
                        }
                        className="w-full border rounded px-3 py-2"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      onClick={clearFilters}
                      className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                    >
                      Clear
                    </button>
                    <button
                      onClick={applyFilters}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Apply
                    </button>
                  </div>
                </div>

                {rcHistory.length === 0 ? (
                  <div className="text-center py-4">
                    No records found matching your filters
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full table-auto text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-2 border">Sl No</th>
                            <th className="px-4 py-2 border">Vehicle Chassis No.</th>
                            <th className="px-4 py-2 border">Type</th>
                            <th className="px-4 py-2 border">Date & Time</th>
                            <th className="px-4 py-2 border">Status</th>
                            {/* <th className="px-4 py-2 border">Action</th> */}
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedData.map((item, index) => (
                            <tr
                              key={index}
                              className="text-center hover:bg-gray-50"
                            >
                              <td className="px-4 py-2 border">
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </td>
                              <td className="px-4 py-2 border">
                                {item.vehicleNo}
                              </td>
                              <td className="px-4 py-2 border">
                                {item.documentType}
                              </td>
                              <td className="px-4 py-2 border">
                                {new Date(item.date).toLocaleString()}
                              </td>
                              <td className="px-4 py-2 border">
                                <span
                                  className={`px-2 py-1 rounded text-white text-xs ${
                                    item.status.toLowerCase() === "success"
                                      ? "bg-green-500"
                                      : "bg-red-500"
                                  }`}
                                >
                                  {item.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="flex flex-col md:flex-row items-center justify-between mt-4">
                      <span className="text-sm text-gray-500">
                        Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                        {Math.min(currentPage * itemsPerPage, rcHistory.length)}{" "}
                        of {rcHistory.length} entries
                      </span>

                      <div className="flex gap-2 mt-2 md:mt-0">
                        <button
                          onClick={handlePrev}
                          disabled={currentPage === 1}
                          className="px-3 py-1 border rounded text-sm disabled:opacity-50"
                        >
                          Prev
                        </button>

                        {visiblePages[0] > 1 && (
                          <span className="px-2">...</span>
                        )}
                        {visiblePages.map((p) => (
                          <button
                            key={p}
                            onClick={() => setCurrentPage(p)}
                            className={`px-3 py-1 border rounded text-sm ${
                              currentPage === p ? "bg-blue-600 text-white" : ""
                            }`}
                          >
                            {p}
                          </button>
                        ))}
                        {visiblePages[visiblePages.length - 1] < totalPages && (
                          <span className="px-2">...</span>
                        )}

                        <button
                          onClick={handleNext}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1 border rounded text-sm disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>{" "}
    </>
  );
}
